import React, { useEffect, useState } from "react";
import { isEmpty } from "lodash";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Label,
  Input,
  FormFeedback,
  Form,
} from "reactstrap";

import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import avatar from "../../assets/images/users/multi-user.jpg";
import { editProfile, resetProfileFlag } from "../../slices/thunks";
import { createSelector } from "reselect";
import { getUserRole } from "../../helpers/role_helper";
import { getLoggedinUser } from "../../api";
import { getSellerMe, updateSeller } from "../../api/ecommerce";
import { toast } from "react-toastify";

/** Bank details for sellers — same `sellers` row as admin /sellers (GET /api/sellers/me) */
function SellerBankCard() {
  const [sellerId, setSellerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bank, setBank] = useState({
    bank_name: "",
    branch: "",
    account_holder_name: "",
    account_number: "",
    ifsc_code: "",
  });

  useEffect(() => {
    let cancelled = false;
    getSellerMe()
      .then((row) => {
        if (cancelled || !row || typeof row !== "object") return;
        setSellerId(row.id || row._id);
        setBank({
          bank_name: row.bank_name || "",
          branch: row.branch || "",
          account_holder_name: row.account_holder_name || "",
          account_number: row.account_number || "",
          ifsc_code: row.ifsc_code || "",
        });
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load seller profile.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setBank((b) => ({ ...b, [name]: value }));
  };

  const saveBank = async (e) => {
    e.preventDefault();
    if (!sellerId) {
      toast.error("Seller record not found. Contact support.");
      return;
    }
    setSaving(true);
    try {
      await updateSeller({
        id: sellerId,
        bank_name: bank.bank_name || null,
        branch: bank.branch || null,
        account_holder_name: bank.account_holder_name || null,
        account_number: bank.account_number || null,
        ifsc_code: bank.ifsc_code || null,
      });
      toast.success("Bank details saved.");
    } catch (err) {
      toast.error(err?.error || err?.message || "Could not save bank details.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="mt-4">
        <CardBody>
          <h4 className="card-title mb-3">Bank details (payouts)</h4>
          <p className="text-muted mb-0">Loading…</p>
        </CardBody>
      </Card>
    );
  }

  if (!sellerId) {
    return (
      <Card className="mt-4">
        <CardBody>
          <h4 className="card-title mb-3">Bank details (payouts)</h4>
          <p className="text-muted mb-0">
            No seller profile is linked to your account yet. An administrator may need to create your seller record
            first.
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <CardBody>
        <h4 className="card-title mb-3">Bank details (payouts)</h4>
        <p className="text-muted small mb-4">
          Used for withdrawal payouts. Keep this information accurate.
        </p>
        <Form onSubmit={saveBank}>
          <Row>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Bank name</Label>
                <Input name="bank_name" value={bank.bank_name} onChange={onChange} placeholder="e.g. Barclays" />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Branch</Label>
                <Input name="branch" value={bank.branch} onChange={onChange} placeholder="Optional" />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Account holder name</Label>
                <Input
                  name="account_holder_name"
                  value={bank.account_holder_name}
                  onChange={onChange}
                  placeholder="Name on account"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">Account number</Label>
                <Input name="account_number" value={bank.account_number} onChange={onChange} />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label className="form-label">IFSC / sort code</Label>
                <Input name="ifsc_code" value={bank.ifsc_code} onChange={onChange} />
              </div>
            </Col>
          </Row>
          <Button type="submit" color="primary" disabled={saving}>
            {saving ? "Saving…" : "Save bank details"}
          </Button>
        </Form>
      </CardBody>
    </Card>
  );
}

const UserProfile = () => {
  const dispatch = useDispatch();

  // ---------------- REDUX ----------------
  const selectLayoutState = (state) => state.Profile;

  const userprofileData = createSelector(
    selectLayoutState,
    (state) => ({
      user: state.user,
      success: state.success,
      error: state.error
    })
  );

  const { user, success, error } = useSelector(userprofileData);

  // ---------------- LOCAL STORAGE ----------------
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const profileData = authUser?.data || {};

  // ---------------- FORMIK ----------------
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: user?.first_name ?? profileData?.first_name ?? "Admin",
      last_name: user?.last_name ?? profileData?.last_name ?? "",
      username: user?.username ?? profileData?.username ?? profileData?.email?.split("@")[0] ?? "",
      phone: user?.phone ?? profileData?.phone ?? "",
      idx: user?.id ?? user?._id ?? profileData?.id ?? profileData?._id ?? "1",
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required("First name is required"),
      last_name: Yup.string(),
      username: Yup.string(),
      phone: Yup.string(),
    }),
    onSubmit: (values) => {
      dispatch(editProfile(values));
    }
  });

  // ---------------- UPDATE LOCAL STORAGE AFTER SUCCESS ----------------
  useEffect(() => {
    if (success && user && !isEmpty(authUser)) {
      const updated = { ...authUser };
      if (updated.data) {
        updated.data.first_name = user.first_name;
        updated.data.last_name = user.last_name ?? updated.data.last_name;
        updated.data.username = user.username ?? updated.data.username;
        updated.data.phone = user.phone ?? updated.data.phone;
      }
      localStorage.setItem("authUser", JSON.stringify(updated));

      dispatch(resetProfileFlag());
    }
  }, [success, user, dispatch]);

  document.title = "Profile | Dashboard";

  const isSeller = getUserRole(getLoggedinUser()) === "seller";

  return (
    <div className="page-content mt-lg-5">
      <Container fluid>
        <Row>
          <Col lg="12">

            {error && <Alert color="danger">{error}</Alert>}
            {success && <Alert color="success">Username updated successfully</Alert>}

            {/* PROFILE CARD */}
            <Card>
              <CardBody>
                <div className="d-flex">
                  <div className="mx-3">
                    <img
                      src={avatar}
                      alt=""
                      className="avatar-md rounded-circle img-thumbnail"
                    />
                  </div>
                  <div className="flex-grow-1 align-self-center">
                    <div className="text-muted">
                      <h5>{validation.values.first_name}</h5>
                      <p className="mb-1">Email Id : {profileData?.email}</p>
                      <p className="mb-0">Id No : #{validation.values.idx}</p>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <h4 className="card-title mb-4">Edit Profile</h4>

        {/* FORM */}
        <Card>
          <CardBody>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
              }}
            >
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label">First Name <span className="text-danger">*</span></Label>
                    <Input
                      name="first_name"
                      type="text"
                      placeholder="Enter first name"
                      value={validation.values.first_name}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={validation.touched.first_name && !!validation.errors.first_name}
                    />
                    {validation.touched.first_name && validation.errors.first_name && (
                      <FormFeedback>{validation.errors.first_name}</FormFeedback>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label">Last Name</Label>
                    <Input
                      name="last_name"
                      type="text"
                      placeholder="Enter last name"
                      value={validation.values.last_name}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label">Username</Label>
                    <Input
                      name="username"
                      type="text"
                      placeholder="Enter username"
                      value={validation.values.username}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label className="form-label">Phone</Label>
                    <Input
                      name="phone"
                      type="text"
                      placeholder="Enter phone"
                      value={validation.values.phone}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                    />
                  </div>
                </Col>
              </Row>
              <Input name="idx" value={validation.values.idx} type="hidden" />

              <div className="text-center mt-4">
                <Button type="submit" color="primary">
                  Update Profile
                </Button>
              </div>

            </Form>
          </CardBody>
        </Card>

        {isSeller && <SellerBankCard />}
      </Container>
    </div>
  );
};

export default UserProfile;